/**
 * KOMPA 2.0 — Centralized formatters and financial calculations.
 * ALL components must import from here. Never redeclare fmt().
 */

import { FREQ_DIVISOR } from '@/lib/constants';

/** Format number as European currency: 1.234,56 */
export const fmt = (v) => {
  const parts = Number(v).toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return parts.join(',');
};

/** Format integer with dot separator: 1.234 */
export const fmtInt = (v) => {
  return Number(v).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

/** Goal reference code */
export const goalCode = (goal) => `#KMP-${String(goal.id).slice(-3)}`;

/**
 * Canonical service fee rate: 1.5% si T<=6 meses, 1.0% si T>6.
 * Mirrors backend commission_rate() in utils/plan_engine.py.
 */
export const commissionRate = (months) => ((Number(months) || 0) <= 6 ? 0.015 : 0.010);

/**
 * Calculate cuota breakdown for a goal using the CANONICAL split:
 *   cuota_liquidable (net savings) = M / (T * p)       → amount_savings
 *   cuota_comision   (service fee) = (M * c) / p       → service_fee
 *   cuota_total                    = cuota_liquidable + cuota_comision
 *
 * @param {object} goal - Goal with totalAmount, frequencyId, months, modality, subModality, planCode
 * @param {number} qty - Number of cuotas
 * @param {number|null} subscriptionEur - KC subscription from plan_templates (null = no subscription)
 * @returns {{ cuota: number, cuotaLiquidable: number, cuotaComision: number, subtotal: number, comisionTotal: number, suscripcionUnit: number, suscripcionTotal: number, total: number }}
 */
export const calculateCuotaBreakdown = (goal, qty = 1, subscriptionEur = null) => {
  const M = Number(goal.totalAmount) || 0;
  const T = Number(goal.months) || 0;
  const p = FREQ_DIVISOR[goal.frequencyId] || 1;
  const c = commissionRate(T);

  const cuotaLiquidable = (M > 0 && T > 0 && p > 0) ? M / (T * p) : 0;
  const cuotaComision   = (M > 0 && p > 0) ? (M * c) / p : 0;
  const cuota           = cuotaLiquidable + cuotaComision;

  const subtotal = cuotaLiquidable * qty;        // pure savings portion
  const comisionTotal = cuotaComision * qty;     // commission portion

  const isKC = goal.modality === 'club' || goal.subModality === 'grupal' || goal.planCode === 'acelerado';
  const suscripcionUnit = (isKC && subscriptionEur) ? subscriptionEur / p : 0;
  const suscripcionTotal = suscripcionUnit * qty;

  const total = subtotal + comisionTotal + suscripcionTotal;

  return { cuota, cuotaLiquidable, cuotaComision, subtotal, comisionTotal, suscripcionUnit, suscripcionTotal, total };
};
